/**
 * Helpers for safely adding rows to user-editable tables (Operating Expenses,
 * Amenity Income, Expenses subtables). Prevents the two bugs documented in
 * BUG_NOTES_amenity_expense_compounding.md / CEO video May 2026:
 *
 *  1. ID collision when two add operations fire within the same millisecond
 *     (Date.now() returned the same number, breaking DataGrid keying and the
 *     id-based `setX(prev => prev.map(row => row.id === id ? ... : row))`
 *     lookup in handleCellChange).
 *
 *  2. Duplicate rows when the MUI Autocomplete's onChange and the adjacent
 *     "Add" button's onClick both fire on the same user gesture (e.g.,
 *     clicking the button blurs the Autocomplete, which can re-emit a
 *     previously-committed value through onChange before the click handler
 *     runs).
 *
 * Use `generateRowId()` for new row IDs and a `CommitGuard` instance to
 * suppress rapid same-name re-fires within a short time window.
 */

/**
 * Generate a row ID that is guaranteed unique across consecutive calls within
 * the same tick. Format: "<ms>-<random>" — preserves chronological ordering
 * for debugging while eliminating Date.now() collisions.
 */
export const generateRowId = (): string =>
  `${Date.now()}-${Math.random().toString(36).slice(2, 11)}`;

/**
 * Rejects a commit if the same trimmed name was committed within `windowMs`.
 * One instance per Autocomplete; create via `useRef(new CommitGuard())`.
 */
export class CommitGuard {
  private lastName = "";
  private lastTs = 0;

  /**
   * @returns true if this name should be skipped (already committed within
   *          the window), false if the caller should proceed and record the
   *          commit. Recording happens automatically on a non-skipped call.
   */
  shouldSkip(name: string, windowMs = 250): boolean {
    const trimmed = (name || "").trim().toLowerCase();
    if (!trimmed) return true; // never commit empty names
    const now = Date.now();
    const skip =
      trimmed === this.lastName && now - this.lastTs < windowMs;
    if (!skip) {
      this.lastName = trimmed;
      this.lastTs = now;
    }
    return skip;
  }

  /** Reset internal state (e.g., on unmount or manual clear). */
  reset(): void {
    this.lastName = "";
    this.lastTs = 0;
  }
}
